import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Repeat } from "lucide-react";

// Mon=0..Sun=6 (matches backend convention)
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Small modal for editing which days of the week a habit fires.
//
//   <HabitScheduleEditor
//     open={schedOpen}
//     habit={h}
//     onClose={() => setSchedOpen(false)}
//     onSave={(days) => onEditHabit(h, { schedule_days: days })}
//   />
export default function HabitScheduleEditor({ open, habit, onClose, onSave }) {
    const [days, setDays] = useState(habit?.schedule_days || [0, 1, 2, 3, 4, 5, 6]);
    const [busy, setBusy] = useState(false);

    // Re-sync local draft whenever a NEW habit is opened.
    React.useEffect(() => {
        if (open && habit) setDays(habit.schedule_days || [0, 1, 2, 3, 4, 5, 6]);
    }, [open, habit]);

    if (!open || !habit) return null;

    const toggleDay = (d) => {
        setDays((prev) =>
            prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()
        );
    };

    const presetWeekdays = () => setDays([0, 1, 2, 3, 4]);
    const presetWeekend = () => setDays([5, 6]);
    const presetEveryDay = () => setDays([0, 1, 2, 3, 4, 5, 6]);

    const save = async () => {
        if (busy) return;
        setBusy(true);
        try { await onSave(days.length ? days : [0, 1, 2, 3, 4, 5, 6]); } finally { setBusy(false); }
        onClose();
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-warm-900/40"
                onClick={onClose}
                data-testid="habit-schedule-overlay"
            >
                <motion.div
                    initial={{ scale: 0.95, y: 16, opacity: 0 }}
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.95, y: 16, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 280, damping: 24 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-cream rounded-clay shadow-float p-5 w-full max-w-sm"
                    data-testid="habit-schedule-editor"
                >
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                            <Repeat size={18} className="text-terracotta" />
                            <h3 className="font-display text-warm-900 text-lg leading-tight">
                                Edit schedule
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 rounded-full hover:bg-surface"
                            data-testid="habit-schedule-close"
                            aria-label="Close"
                        >
                            <X size={16} className="text-warm-500" />
                        </button>
                    </div>
                    <p className="font-body text-xs text-warm-500 mb-3 truncate">
                        {habit.title}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mb-4">
                        {DAY_LABELS.map((label, i) => {
                            const on = days.includes(i);
                            return (
                                <button
                                    key={label}
                                    onClick={() => toggleDay(i)}
                                    className={`flex-1 min-w-[42px] px-2 py-2 rounded-xl font-display text-xs transition-all ${
                                        on
                                            ? "bg-terracotta text-cream shadow-clay"
                                            : "bg-surface text-warm-500 hover:text-warm-900"
                                    }`}
                                    data-testid={`habit-schedule-day-${i}`}
                                    aria-pressed={on}
                                >
                                    {label.slice(0, 3)}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-4">
                        <button
                            onClick={presetEveryDay}
                            className="px-3 py-1 rounded-full bg-surface font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="habit-schedule-preset-everyday"
                        >
                            Every day
                        </button>
                        <button
                            onClick={presetWeekdays}
                            className="px-3 py-1 rounded-full bg-surface font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="habit-schedule-preset-weekdays"
                        >
                            Weekdays
                        </button>
                        <button
                            onClick={presetWeekend}
                            className="px-3 py-1 rounded-full bg-surface font-body text-[11px] text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="habit-schedule-preset-weekend"
                        >
                            Weekend
                        </button>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-full font-display text-sm text-warm-500 hover:text-warm-900 transition-colors"
                            data-testid="habit-schedule-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={save}
                            disabled={busy}
                            className="px-4 py-2 rounded-full font-display text-sm bg-terracotta text-cream shadow-clay hover:shadow-float transition-all disabled:opacity-60"
                            data-testid="habit-schedule-save"
                        >
                            Save
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
